import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * hallmark critique · HoldCommandPanel
 * tokens locked from docs/design.md §5 (Filled Dark Pill) + §10 (8-state matrix)
 * states covered:
 *   idle · arming · armed · sending · sent-ack · ack-timeout · error · disabled
 * mobile verified: 320 / 375 / 414 / 768
 * no fake chrome · no italic headers · no shadows · single clay accent per viewport
 *
 * Phase 4: the panel is now a pure VIEW over a state machine that lives
 * in `useHoldCommand` (parent-owned). The parent passes `forcedState` +
 * `onPrimary` and the panel renders the appropriate copy/visual for the
 * current state. `?hold=<state>` still drives Playwright snapshots.
 *
 * Also renders the "recent holds" strip (last 3 accepted responses)
 * beneath the pill — this is the audit-log evidence surface required by
 * the Phase-4 gate deliverable #4.
 */
import { useMemo } from "react";
function visualFor(state, vehicleId, stopId, secs) {
    switch (state) {
        case "idle":
            return {
                label: "Hold Bus",
                body: `Route AS-4 · ${vehicleId} → hold ${secs}s at ${stopId}.`,
                pillBg: "#ffffff",
                pillFg: "#0f0f1c",
                pillBorder: "#ffffff",
                border: "1px solid #ffffff",
                helperFg: "#8e8e95",
                spinner: false,
                disabled: false,
                chipBg: "transparent",
                chipFg: "rgba(255,255,255,0.65)",
                chipLabel: "Idle",
            };
        case "arming":
            return {
                label: "Confirm hold?",
                body: "Tap again within 3s to send. Undo cancels.",
                pillBg: "#000d10",
                pillFg: "#ffffff",
                pillBorder: "#000d10",
                border: "2px solid #bc7155",
                helperFg: "#bc7155",
                spinner: false,
                disabled: false,
                chipBg: "#bc7155",
                chipFg: "#ffffff",
                chipLabel: "Arming",
            };
        case "armed":
            return {
                label: `Send · ${secs}s`,
                body: `Armed. Command ready to dispatch to ${vehicleId}.`,
                pillBg: "#bc7155",
                pillFg: "#ffffff",
                pillBorder: "#bc7155",
                border: "2px solid #bc7155",
                helperFg: "#bc7155",
                spinner: false,
                disabled: false,
                chipBg: "#bc7155",
                chipFg: "#ffffff",
                chipLabel: "Armed",
            };
        case "sending":
            return {
                label: "Sending...",
                body: "Command POST /api/hold in flight.",
                pillBg: "rgba(255,255,255,0.06)",
                pillFg: "#ffffff",
                pillBorder: "rgba(255,255,255,0.35)",
                border: "1px solid rgba(255,255,255,0.35)",
                helperFg: "#8e8e95",
                spinner: true,
                disabled: true,
                chipBg: "rgba(255,255,255,0.16)",
                chipFg: "#ffffff",
                chipLabel: "Sending",
            };
        case "sent-ack":
            return {
                label: "Sent · Ack",
                body: `${vehicleId} acknowledged · holding at ${stopId} for ${secs}s.`,
                pillBg: "transparent",
                pillFg: "#ffffff",
                pillBorder: "rgba(255,255,255,0.85)",
                border: "1px solid rgba(255,255,255,0.85)",
                helperFg: "#ffffff",
                spinner: false,
                disabled: true,
                chipBg: "#ffffff",
                chipFg: "#0f0f1c",
                chipLabel: "Ack",
            };
        case "ack-timeout":
            return {
                label: "Retry send",
                body: "No ack in 5s. Radio or driver console offline?",
                pillBg: "#ffffff",
                pillFg: "#0f0f1c",
                pillBorder: "#ffffff",
                border: "1px solid #ffffff",
                helperFg: "#ffffff",
                spinner: false,
                disabled: false,
                chipBg: "#ffffff",
                chipFg: "#0f0f1c",
                chipLabel: "Timeout",
            };
        case "error":
            return {
                label: "Retry",
                body: "HTTP error · fleet gateway rejected the hold. Retry or contact ops.",
                pillBg: "#bc7155",
                pillFg: "#ffffff",
                pillBorder: "#bc7155",
                border: "2px solid #bc7155",
                helperFg: "#bc7155",
                spinner: false,
                disabled: false,
                chipBg: "#bc7155",
                chipFg: "#ffffff",
                chipLabel: "Error",
            };
        case "disabled":
        default:
            return {
                label: "Hold unavailable",
                body: "No active alert. Waiting for the next bunch forecast.",
                pillBg: "#8e8e95",
                pillFg: "#ffffff",
                pillBorder: "#8e8e95",
                border: "1px solid #d5d3d4",
                helperFg: "#8e8e95",
                spinner: false,
                disabled: true,
                chipBg: "transparent",
                chipFg: "#8e8e95",
                chipLabel: "Disabled",
            };
    }
}
const rootStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    minHeight: 160,
    color: "var(--color-pure-white)",
};
const bodyStyle = {
    fontSize: 13,
    lineHeight: 1.45,
    color: "rgba(255,255,255,0.75)",
    margin: 0,
};
const chipStyle = (bg, fg) => ({
    alignSelf: "flex-start",
    padding: "4px 10px",
    border: bg === "transparent" ? "1px solid rgba(255,255,255,0.45)" : "none",
    borderRadius: 1000,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: fg,
    backgroundColor: bg,
});
const pillStyle = (v) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    justifyContent: "center",
    padding: "14px 22px",
    borderRadius: 1000,
    backgroundColor: v.pillBg,
    color: v.pillFg,
    border: `1px solid ${v.pillBorder}`,
    fontFamily: "var(--font-display)",
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: "0.02em",
    lineHeight: 1,
    whiteSpace: "nowrap",
    transition: "background-color 120ms ease-out, color 120ms ease-out",
    cursor: v.disabled ? "not-allowed" : "pointer",
    opacity: v.disabled ? 0.75 : 1,
    width: "100%",
});
const spinnerStyle = {
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: "50%",
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "#ffffff",
    animation: "bb-spin 0.7s linear infinite",
};
export function HoldCommandPanel({ forcedState, suggestedSeconds = 42, vehicleId = "AS-4-B02", stopId = "AS-4-S04", onPrimary, history = [], lastError = null, }) {
    const state = forcedState ?? "idle";
    const visual = useMemo(() => visualFor(state, vehicleId, stopId, suggestedSeconds), [state, vehicleId, stopId, suggestedSeconds]);
    return (_jsxs("div", { style: rootStyle, "data-testid": "hold-panel", "data-hold-state": state, children: [_jsxs("div", { style: {
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 8,
                }, children: [_jsx("div", { style: {
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.5)",
                        }, children: "Hold Command" }), _jsx("span", { style: chipStyle(visual.chipBg, visual.chipFg), children: visual.chipLabel })] }), _jsxs("h3", { style: {
                    fontSize: 20,
                    lineHeight: 1.1,
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    margin: 0,
                    color: "var(--color-pure-white)",
                }, children: [vehicleId, " at ", stopId, " \u00B7 ", suggestedSeconds, "s"] }), _jsx("p", { style: bodyStyle, children: visual.body }), state === "error" && lastError && (_jsx("p", { "data-testid": "hold-error-detail", style: {
                    margin: 0,
                    fontSize: 12,
                    color: "#f4c3b1",
                    lineHeight: 1.4,
                }, children: lastError })), _jsx("div", { style: { display: "flex", gap: 8, marginTop: "auto", width: "100%" }, children: _jsxs("button", { type: "button", onClick: onPrimary, disabled: visual.disabled, style: { ...pillStyle(visual), border: visual.border, borderRadius: 1000 }, "data-testid": "hold-primary-cta", children: [visual.spinner && _jsx("span", { "aria-hidden": "true", style: spinnerStyle }), visual.label] }) }), history.length > 0 && (_jsxs("div", { "data-testid": "hold-history", style: {
                    marginTop: 10,
                    paddingTop: 10,
                    borderTop: "1px solid rgba(255,255,255,0.12)",
                }, children: [_jsxs("div", { style: {
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.55)",
                            marginBottom: 6,
                        }, children: ["Recent holds \u00B7 ", history.length] }), _jsx("ul", { style: {
                            listStyle: "none",
                            margin: 0,
                            padding: 0,
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                        }, children: history.slice(0, 3).map((h) => (_jsxs("li", { "data-testid": `hold-history-${h.id}`, style: {
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 8,
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                                color: "rgba(255,255,255,0.75)",
                                fontFamily: "var(--font-display)",
                            }, children: [_jsxs("span", { children: ["#", h.id, " \u00B7 ", h.vehicle_id] }), _jsxs("span", { children: [h.stop_id, " \u00B7 ", h.hold_seconds, "s"] })] }, h.id))) })] })), _jsx("style", { children: `
        @keyframes bb-spin { to { transform: rotate(360deg); } }
      ` })] }));
}
